import { config } from "../Environment";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface CalibrationResult {
  question: string;
  followUpHint: string;
}

export interface DefinitionAssistResult {
  suggestion: string;
  examplePrompt: string;
}

export interface TraitLinkSuggestion {
  traitId: string;
  traitName: string;
  reason: string;
}

export interface LeverageAnalysisResult {
  traitId: string;
  traitName: string;
  leverageScore: number;
  reasoning: string;
}

export interface TraitCompanionInsight {
  content: string;
  relatedTraitNames: string[];
  insightType: "progress_summary" | "gap_detection" | "cross_trait_synergy" | "encouragement";
}

// ─── API Functions ───────────────────────────────────────────────────────────

/**
 * Generate a first-person self-assessment statement for a single trait level.
 */
export const calibrateTrait = async (
  retryFetch: Function,
  data: { traitId: string; traitName: string; level: number }
): Promise<CalibrationResult> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/traits/ai/calibrate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to get calibration question");
  }

  return response.json();
};

/**
 * Generate first-person self-assessment statements for all 3 non-aspiration levels at once.
 * Returns a map keyed by level number (1, 2, 3).
 */
export const calibrateTraitBatch = async (
  retryFetch: Function,
  data: { traitId?: string; traitName: string; identityName?: string; language?: string }
): Promise<Record<number, CalibrationResult>> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/traits/ai/calibrate-batch`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to get batch calibration");
  }

  return response.json();
};

/**
 * Get AI assistance writing or refining a personal definition for a trait.
 */
export const assistTraitDefinition = async (
  retryFetch: Function,
  data: {
    traitId: string;
    traitName: string;
    category: string;
    currentDefinition?: string;
    level: number;
  }
): Promise<DefinitionAssistResult> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/traits/ai/assist-definition`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to get definition assistance");
  }

  return response.json();
};

/**
 * Suggest which traits an action item or insight could be linked to.
 */
export const suggestTraitLink = async (
  retryFetch: Function,
  data: { text: string; entityType?: string; spaceId: string }
): Promise<{ suggestions: TraitLinkSuggestion[] }> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/traits/ai/suggest-link`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to get trait link suggestions");
  }

  return response.json();
};

/**
 * Analyze which traits have the highest developmental leverage.
 */
export const traitLeverageAnalysis = async (
  retryFetch: Function,
  spaceId: string
): Promise<{ analysis: LeverageAnalysisResult[]; message?: string }> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/traits/ai/leverage`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ spaceId }),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to get leverage analysis");
  }

  return response.json();
};

/**
 * Generate a trait-aware companion insight message.
 */
export const traitCompanionInsight = async (
  retryFetch: Function,
  spaceId: string
): Promise<{ insight: TraitCompanionInsight | null; relatedTraitIds?: string[] }> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/traits/ai/companion-insight`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ spaceId }),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to get trait companion insight");
  }

  return response.json();
};
